import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Check, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppearanceMenu } from "@/components/AppearanceMenu";
import { Logo } from "@/components/Logo";
import { Loading } from "@/components/States";
import { BadgeMedal } from "@/components/BadgeMedal";
import { ShareButtons, CERT_KINDS, awardedDate } from "@/pages/Badges";
import { api, fetchSubjects } from "@/lib/api";

const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });

function VerifiedPanel({ b }) {
  if (!b.verified) {
    return (
      <div data-testid="badge-unverified" className="mt-6 flex w-full items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-left text-sm text-amber-900">
        <ShieldAlert className="h-6 w-6 shrink-0" />This certificate exists but the holder's account is no longer active, so it can't be verified.
      </div>
    );
  }
  const label = CERT_KINDS.includes(b.kind) ? "Verified certificate" : "Verified badge";
  return (
    <div data-testid="badge-verified" className="mt-6 w-full rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-left">
      <div className="flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600"><Check className="h-5 w-5 text-white" strokeWidth={3} /></span>
        <div>
          <p data-testid="badge-verified-label" className="font-bold text-emerald-800">{label}</p>
          <p className="text-xs text-emerald-700">Issued by {b.issuer} · checked live against IDDCR LearnAI records</p>
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-1 border-t border-emerald-200 pt-3 text-xs sm:grid-cols-2">
        <div><dt className="text-emerald-700">Certificate ID</dt><dd data-testid="badge-verified-id" className="break-all font-mono text-emerald-900">{b.id}</dd></div>
        <div><dt className="text-emerald-700">Verified on</dt><dd className="text-emerald-900">{fmtDate(b.verified_at)}</dd></div>
      </dl>
    </div>
  );
}

function VerifyLookup() {
  const nav = useNavigate();
  const [id, setId] = useState("");
  return (
    <form data-testid="verify-lookup" className="mt-8 flex w-full max-w-lg gap-2" onSubmit={(e) => { e.preventDefault(); if (id.trim()) nav(`/badge/${encodeURIComponent(id.trim())}`); }}>
      <Input data-testid="verify-lookup-input" value={id} onChange={(e) => setId(e.target.value)} placeholder="Verify another certificate ID" className="bg-surface" />
      <Button data-testid="verify-lookup-btn" type="submit" variant="outline">Verify</Button>
    </form>
  );
}

export default function BadgePublic() {
  const { id } = useParams();
  const [b, setB] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    setB(null); setMissing(false);
    Promise.all([api.get(`/public/badges/${encodeURIComponent(id)}`), fetchSubjects()])
      .then(([r, subjects]) => setB({ ...r.data, icon: subjects.find((s) => s.id === r.data.subject_id)?.icon }))
      .catch(() => setMissing(true));
  }, [id]);

  return (
    <div className="grid-bg flex min-h-screen flex-col items-center bg-canvas px-4 py-10">
      <div className="flex w-full max-w-lg items-center justify-between"><Logo className="h-10" /><AppearanceMenu /></div>
      <div className="my-auto w-full max-w-lg py-10">
        {missing ? (
          <div data-testid="badge-public-missing" className="flex flex-col items-center gap-3 rounded-3xl border border-red-200 bg-surface p-10 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100"><ShieldAlert className="h-6 w-6 text-red-600" /></span>
            <p className="font-bold text-red-800">Certificate not found</p>
            <p className="text-sm text-ink-2">No IDDCR LearnAI certificate matches this ID. Please check the ID or link and try again.</p>
          </div>
        ) : !b ? <Loading /> : (
          <div data-testid="badge-public" className="flex flex-col items-center rounded-3xl border border-line bg-surface p-10 text-center shadow-[0_30px_60px_-30px_rgba(10,31,68,.35)] fade-up">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-aqua">IDDCR LearnAI · Verified achievement</p>
            <div className="my-8"><BadgeMedal badge={b} size="lg" /></div>
            <h1 data-testid="badge-public-title" className="text-3xl font-extrabold text-ink">{b.title}</h1>
            <p className="mt-3 text-ink-2">Awarded to <b data-testid="badge-public-name" className="text-ink">{b.user_name}</b></p>
            <p className="mt-2 text-sm text-ink-3">{b.description}</p>
            <p className="mt-1 text-xs text-ink-3">{fmtDate(awardedDate(b.awarded_at))}</p>
            <VerifiedPanel b={b} />
            <div className="mt-8"><ShareButtons badge={b} /></div>
            {b.showcase_handle && <Link data-testid="badge-public-showcase-link" to={`/learner/${b.showcase_handle}`} className="mt-4 text-sm font-medium text-ink underline-offset-4 hover:underline">See all of {b.user_name}'s certificates</Link>}
            <Button asChild variant="link" className="mt-6 text-aqua"><Link data-testid="badge-public-cta" to="/register">Start learning clinical research on LearnAI</Link></Button>
          </div>
        )}
        <VerifyLookup />
      </div>
    </div>
  );
}
